import React from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { EditorView, basicSetup } from 'codemirror';
import { EditorState } from '@codemirror/state';
import {
  ArrowRight,
  Award,
  BarChart3,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Code2,
  CircleAlert,
  Flame,
  Gamepad2,
  GraduationCap,
  Keyboard,
  LayoutDashboard,
  Languages,
  Medal,
  Menu,
  RefreshCcw,
  Sparkles,
  X,
  Target,
  Timer,
  Trophy,
  UserCircle2,
  Zap
} from 'lucide-react';
import { applySeo, buildAbsoluteUrl } from '../utils/seo';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchDailyChallenge,
  fetchPracticeLeaderboard,
  fetchPracticeSummary,
  fetchRandomPracticeChallenge,
  submitPracticeSession
} from '../services/practiceArenaService';

const NAV_LINKS = [
  { label: 'Practice Arena', path: '/practice-arena' },
  { label: 'Colleges', path: '/dashboard' },
  { label: 'Comparator', path: '/comparator' },
  { label: 'Compiler', path: '/compiler' },
  { label: 'Tutorials', path: '/tutorials' },
  { label: 'Blogs', path: '/blogs' }
];

const PRACTICE_MODES = [
  { key: 'coding', label: 'Coding', icon: Code2, path: '/practice-arena/coding' },
  { key: 'english', label: 'English', icon: Languages, path: '/practice-arena/english' }
];

const LANGUAGE_DATA = {
  javascript: {
    label: 'JavaScript',
    route: 'javascript',
    color: 'from-purple-600 to-pink-500',
    preview: `for (let i = 0; i < array.length; i++) {
  if (array[i] % 2 === 0) {
    evenNumbers.push(array[i]);
  } else {
    oddNumbers.push(array[i]);
  }
}`,
    target: `for (let i = 0; i < array.length; i++) {
  if (array[i] % 2 === 0) {
    evenNumbers.push(array[i]);
  } else {
    oddNumbers.push(array[i]);
  }
}`,
    summary: 'Filter values, handle arrays, and keep your JavaScript rhythm tight.'
  },
  java: {
    label: 'Java',
    route: 'java',
    color: 'from-orange-500 to-amber-400',
    preview: `for (int i = 0; i < numbers.length; i++) {
    if (numbers[i] > threshold) {
        selected.add(numbers[i]);
    }
}`,
    target: `for (int i = 0; i < numbers.length; i++) {
    if (numbers[i] > threshold) {
        selected.add(numbers[i]);
    }
}`,
    summary: 'Practice loops, collections, and clean Java syntax.'
  },
  python: {
    label: 'Python',
    route: 'python',
    color: 'from-sky-500 to-cyan-400',
    preview: `for index, value in enumerate(items):
    if value % 2 == 0:
        evens.append(value)
    else:
        odds.append(value)`,
    target: `for index, value in enumerate(items):
    if value % 2 == 0:
        evens.append(value)
    else:
        odds.append(value)`,
    summary: 'Build confidence with indentation, lists, and readable code flow.'
  },
  cpp: {
    label: 'C++',
    route: 'cpp',
    color: 'from-indigo-500 to-violet-400',
    preview: `for (int i = 0; i < values.size(); ++i) {
    if (values[i] < limit) {
        filtered.push_back(values[i]);
    }
}`,
    target: `for (int i = 0; i < values.size(); ++i) {
    if (values[i] < limit) {
        filtered.push_back(values[i]);
    }
}`,
    summary: 'Sharpen pointers, vectors, and operator-heavy typing speed.'
  },
  sql: {
    label: 'SQL',
    route: 'sql',
    color: 'from-emerald-500 to-teal-400',
    preview: `SELECT name, score
FROM practice_sessions
WHERE accuracy >= 95
ORDER BY wpm DESC;`,
    target: `SELECT name, score
FROM practice_sessions
WHERE accuracy >= 95
ORDER BY wpm DESC;`,
    summary: 'Move faster through query structure, clauses, and ordering.'
  }
};

const ENGLISH_SECTIONS = {
  general: {
    label: 'General English',
    paragraph:
      'The best typing practice feels like a calm daily habit: clear prompts, accurate feedback, and enough variety to keep the mind active without feeling overloaded.',
    subtitle: 'Balanced, readable sentences for day-to-day speed building.'
  },
  technology: {
    label: 'Technology',
    paragraph:
      'Modern teams move faster when people can type technical language, product notes, and release updates with confidence, accuracy, and consistent pacing.',
    subtitle: 'Practice the vocabulary used in software and digital work.'
  },
  business: {
    label: 'Business',
    paragraph:
      'Clear business typing helps people move through emails, reports, proposals, and meeting notes with less friction and more confidence.',
    subtitle: 'Professional language for everyday workplace communication.'
  },
  motivation: {
    label: 'Motivation',
    paragraph:
      'Small improvements compound. A few focused minutes each day can turn loose, hesitant typing into a smooth habit that supports interviews, study, and professional work.',
    subtitle: 'Positive, fluent copy for steady practice sessions.'
  },
  student: {
    label: 'Student Life',
    paragraph:
      'From class notes and assignments to discussion posts and scholarship forms, students benefit from typing that is accurate, relaxed, and easy to sustain for longer stretches.',
    subtitle: 'Everyday language shaped for learners.'
  }
};

const LEADERBOARD_ROWS = {
  daily: [
    { name: 'Aryan Verma', wpm: 112, accent: 'gold' },
    { name: 'Priya Singh', wpm: 98, accent: 'slate' },
    { name: 'Dev Sharma', wpm: 91, accent: 'slate' },
    { name: 'Kunal Singh (You)', wpm: 62, accent: 'purple', highlight: true },
    { name: 'Rohan Mehta', wpm: 58, accent: 'slate' }
  ],
  weekly: [
    { name: 'Priya Singh', wpm: 106, accent: 'gold' },
    { name: 'Aryan Verma', wpm: 103, accent: 'slate' },
    { name: 'Kunal Singh (You)', wpm: 78, accent: 'purple', highlight: true },
    { name: 'Dev Sharma', wpm: 74, accent: 'slate' },
    { name: 'Rohan Mehta', wpm: 69, accent: 'slate' }
  ],
  allTime: [
    { name: 'Aryan Verma', wpm: 118, accent: 'gold' },
    { name: 'Priya Singh', wpm: 113, accent: 'slate' },
    { name: 'Dev Sharma', wpm: 109, accent: 'slate' },
    { name: 'Kunal Singh (You)', wpm: 84, accent: 'purple', highlight: true },
    { name: 'Rohan Mehta', wpm: 81, accent: 'slate' }
  ]
};

const TIME_OPTIONS = [30, 45, 60, 90];
const LANGUAGE_KEYS = Object.keys(LANGUAGE_DATA);
const ENGLISH_KEYS = Object.keys(ENGLISH_SECTIONS);
const ENGLISH_BADGE_COLORS = {
  general: 'from-purple-600 to-pink-500',
  technology: 'from-sky-500 to-cyan-400',
  business: 'from-orange-500 to-amber-400',
  motivation: 'from-rose-500 to-orange-400',
  student: 'from-emerald-500 to-teal-400'
};

function lcsLength(a, b) {
  const m = a.length;
  const n = b.length;
  let prev = new Array(n + 1).fill(0);
  let curr = new Array(n + 1).fill(0);
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      curr[j] = a[i - 1] === b[j - 1] ? prev[j - 1] + 1 : Math.max(prev[j], curr[j - 1]);
    }
    const tmp = prev; prev = curr; curr = tmp;
    curr.fill(0);
  }
  return prev[n];
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function tokenizeForEnglishScore(value = '') {
  return String(value)
    .toLowerCase()
    .match(/[a-z0-9']+/g) || [];
}

function buildTypingMetrics({ practiceMode, targetText, typedText, timeLimit, timeLeft }) {
  const elapsedSeconds = Math.max(1, timeLimit - timeLeft);
  const hasTyped = typedText.length > 0;

  if (practiceMode === 'english') {
    const targetWords = tokenizeForEnglishScore(targetText);
    const typedWords = tokenizeForEnglishScore(typedText);

    const correctWords = typedWords.length === 0 ? 0 : lcsLength(typedWords, targetWords);
    const mistakes = typedWords.length - correctWords;

    const completedCount = Math.min(correctWords, targetWords.length);
    const pendingCount = Math.max(0, targetWords.length - typedWords.length);
    const progressPercent = clamp((typedWords.length / Math.max(targetWords.length, 1)) * 100, 0, 100);
    const accuracy = !hasTyped ? 0 : Math.round((correctWords / typedWords.length) * 100);
    const wpm = Math.max(0, Math.round(correctWords / (elapsedSeconds / 60)));

    return {
      accuracy,
      completedCount,
      elapsedSeconds,
      mistakes,
      pendingCount,
      progressPercent,
      targetCount: targetWords.length,
      typedCount: typedWords.length,
      unitLabel: 'words',
      wpm
    };
  }

  const normalizedTarget = targetText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const normalizedTyped = typedText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  const strippedTarget = normalizedTarget.replace(/\s/g, '');
  const strippedTyped = normalizedTyped.replace(/\s/g, '');

  const correctChars = strippedTyped.length === 0 ? 0 : lcsLength(strippedTyped, strippedTarget);
  const mistakes = strippedTyped.length - correctChars;

  const completedCount = clamp(correctChars, 0, strippedTarget.length);
  const pendingCount = Math.max(0, strippedTarget.length - strippedTyped.length);
  const progressPercent = clamp((strippedTyped.length / Math.max(strippedTarget.length, 1)) * 100, 0, 100);
  const accuracy = !hasTyped
    ? 0
    : Math.round((correctChars / strippedTyped.length) * 100);
  const wpm = Math.max(0, Math.round((correctChars / 5) / (elapsedSeconds / 60)));

  return {
    accuracy,
    completedCount,
    elapsedSeconds,
    mistakes,
    pendingCount,
    progressPercent,
    targetCount: strippedTarget.length,
    typedCount: strippedTyped.length,
    unitLabel: 'characters',
    wpm
  };
}

function formatSeconds(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatCountdown(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function getSectionConfig(sectionKey = '') {
  const normalized = String(sectionKey || '').toLowerCase();

  if (normalized === 'english') {
    return { mode: 'english', language: 'javascript', focus: 'practice' };
  }

  if (normalized === 'daily-challenge') {
    return { mode: 'coding', language: 'javascript', focus: 'daily' };
  }

  if (normalized === 'leaderboard') {
    return { mode: 'coding', language: 'javascript', focus: 'leaderboard' };
  }

  if (LANGUAGE_KEYS.includes(normalized)) {
    return { mode: 'coding', language: normalized, focus: 'practice' };
  }

  return { mode: 'coding', language: 'javascript', focus: 'practice' };
}

function createSparks(seed) {
  return Array.from({ length: 10 }, (_, index) => {
    const base = (seed + index * 17) % 40;
    return 16 + base;
  });
}

function MiniSparkline({ values, color }) {
  const points = values
    .map((value, index) => `${(index / (values.length - 1)) * 100},${100 - value}`)
    .join(' ');

  return (
    <svg viewBox="0 0 100 100" className="h-10 w-full" preserveAspectRatio="none" aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PracticeArenaPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const params = useParams();
  const practiceStackRef = React.useRef(null);
  const sectionKey = params.section || '';
  const sectionConfig = getSectionConfig(sectionKey);
  const authUserDisplayName = user?.displayName || user?.email?.split('@')[0] || '';

  const [practiceMode, setPracticeMode] = React.useState(sectionConfig.mode);
  const [selectedLanguage, setSelectedLanguage] = React.useState(sectionConfig.language);
  const [englishTopic, setEnglishTopic] = React.useState('general');
  const [timeLimit, setTimeLimit] = React.useState(60);
  const [timeLeft, setTimeLeft] = React.useState(60);
  const [typedText, setTypedText] = React.useState('');
  const [leaderboardTab, setLeaderboardTab] = React.useState('daily');
  const [dailyCountdownMs, setDailyCountdownMs] = React.useState(0);
  const [isRunning, setIsRunning] = React.useState(false);
  const [sessionResult, setSessionResult] = React.useState(null);
  const [previewPanelRatio, setPreviewPanelRatio] = React.useState(0.5);
  const [challengeNonce, setChallengeNonce] = React.useState(0);
  const [practiceChallenge, setPracticeChallenge] = React.useState(null);
  const [dailyChallenge, setDailyChallenge] = React.useState(null);
  const [dailyCompletedXP, setDailyCompletedXP] = React.useState(null);
  const [leaderboardRows, setLeaderboardRows] = React.useState(LEADERBOARD_ROWS.daily);
  const [isFetchingChallenge, setIsFetchingChallenge] = React.useState(false);
  const [isSubmittingResult, setIsSubmittingResult] = React.useState(false);
  const [practiceSummary, setPracticeSummary] = React.useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = React.useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const typingEditorRef = React.useRef(null);
  const typingViewRef = React.useRef(null);
  const isFinishingRef = React.useRef(false);

  React.useEffect(() => {
    const nextState = getSectionConfig(sectionKey);
    setPracticeMode(nextState.mode);
    setSelectedLanguage(nextState.language);
    setTypedText('');
    setTimeLimit(60);
    setTimeLeft(60);
    setIsRunning(false);
    setSessionResult(null);
    setPreviewPanelRatio(0.5);

    if (nextState.focus === 'daily') {
      setLeaderboardTab('daily');
      setChallengeNonce((c) => c + 1);
    } else if (nextState.focus === 'leaderboard') {
      setLeaderboardTab('daily');
    }
  }, [sectionKey]);

  React.useEffect(() => {
    const dailyTarget = () => {
      const now = new Date();
      const nextReset = new Date(now);
      nextReset.setHours(23, 59, 59, 999);
      if (nextReset.getTime() <= now.getTime()) {
        nextReset.setDate(nextReset.getDate() + 1);
      }
      return nextReset.getTime() - now.getTime();
    };

    const updateCountdown = () => setDailyCountdownMs(dailyTarget());

    updateCountdown();
    const interval = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(interval);
  }, []);

  React.useEffect(() => {
    if (!isRunning || timeLeft <= 0 || sessionResult) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setTimeLeft((current) => {
        if (current <= 1) {
          window.clearInterval(interval);
          setIsRunning(false);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isRunning, sessionResult]);

  React.useEffect(() => {
    let active = true;

    const loadChallenge = async () => {
      setIsFetchingChallenge(true);

      try {
        const response = await fetchRandomPracticeChallenge(
          practiceMode === 'english'
            ? { mode: 'english', category: englishTopic, duration: timeLimit, challengeNonce }
            : { mode: 'code', language: selectedLanguage, duration: timeLimit, challengeNonce }
        );

        if (!active) {
          return;
        }

        setPracticeChallenge(response?.data || null);
      } catch (error) {
        if (active) {
          setPracticeChallenge(null);
        }
      } finally {
        if (active) {
          setIsFetchingChallenge(false);
        }
      }
    };

    loadChallenge();

    return () => {
      active = false;
    };
  }, [challengeNonce, englishTopic, practiceMode, selectedLanguage, timeLimit]);

  React.useEffect(() => {
    let active = true;

    const loadDailyChallenge = async () => {
      try {
        const response = await fetchDailyChallenge();
        if (active) {
          setDailyChallenge(response?.data || null);
          if (response?.data?.completedToday) {
            setDailyCompletedXP((prev) => prev ?? response?.data?.xpEarned ?? response?.data?.bonusXP ?? null);
          }
        }
      } catch {
        if (active) {
          setDailyChallenge(null);
        }
      }
    };

    loadDailyChallenge();

    return () => {
      active = false;
    };
  }, [isAuthenticated, sectionKey]);

  React.useEffect(() => {
    let active = true;

    const loadLeaderboard = async () => {
      try {
        const period = leaderboardTab === 'allTime' ? 'alltime' : leaderboardTab;
        const response = await fetchPracticeLeaderboard(period);
        if (active) {
          setLeaderboardRows(response?.data?.items || []);
        }
      } catch {
        if (active) {
          setLeaderboardRows(LEADERBOARD_ROWS[leaderboardTab] || LEADERBOARD_ROWS.daily);
        }
      }
    };

    loadLeaderboard();

    return () => {
      active = false;
    };
  }, [leaderboardTab]);

  React.useEffect(() => {
    if (!isAuthenticated) {
      setDailyCompletedXP(null);
    }
  }, [isAuthenticated]);

  React.useEffect(() => {
    let active = true;

    const loadSummary = async () => {
      if (!isAuthenticated) {
        if (active) {
          setPracticeSummary(null);
        }
        return;
      }

      setIsLoadingSummary(true);

      try {
        const response = await fetchPracticeSummary();
        if (active) {
          setPracticeSummary(response?.data || null);
        }
      } catch {
        if (active) {
          setPracticeSummary(null);
        }
      } finally {
        if (active) {
          setIsLoadingSummary(false);
        }
      }
    };

    loadSummary();

    return () => {
      active = false;
    };
  }, [isAuthenticated]);

  const totalXP = isAuthenticated ? (sessionResult?.user?.totalXP ?? practiceSummary?.user?.totalXP ?? 0) : 0;
  const XP_PER_LEVEL = 1000;
  const currentLevel = Math.floor(totalXP / XP_PER_LEVEL) + 1;
  const xpIntoLevel = totalXP % XP_PER_LEVEL;
  const xpProgressPercent = Math.round((xpIntoLevel / XP_PER_LEVEL) * 100);

  const isDailyFocus = sectionKey === 'daily-challenge';
  const isDailyChallengeFocus = isDailyFocus;
  // Only lock if dailyChallenge data has loaded (not null) AND completed, OR XP was just earned this session
  const dailyAlreadyCompleted = isDailyFocus && (
    (dailyChallenge !== null && dailyChallenge?.completedToday === true) ||
    dailyCompletedXP !== null
  );
  const isLeaderboardFocus = sectionKey === 'leaderboard';
  const dailyChallengeRef = React.useRef(null);
  const leaderboardRef = React.useRef(null);

  React.useEffect(() => {
    if (isDailyChallengeFocus && dailyChallengeRef.current) {
      setTimeout(() => {
        dailyChallengeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
    if (isLeaderboardFocus && leaderboardRef.current) {
      setTimeout(() => {
        leaderboardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [isDailyChallengeFocus, isLeaderboardFocus]);

  const languageConfig = LANGUAGE_DATA[selectedLanguage] || LANGUAGE_DATA.javascript;
  const englishConfig = ENGLISH_SECTIONS[englishTopic] || ENGLISH_SECTIONS.general;
  const isEnglishMode = practiceMode === 'english';
  const currentUserName = isAuthenticated
    ? (practiceSummary?.user?.name || authUserDisplayName)
    : 'Guest User';
  const currentStreakLabel = isAuthenticated
    ? `${practiceSummary?.user?.currentStreak || 0} Day Streak`
    : '1 Day Streak';
  const bestWpmValue = isAuthenticated
    ? String(practiceSummary?.user?.averageWPM || 0)
    : practiceMode === 'english'
      ? '74'
      : '78';
  const bestAccuracyValue = isAuthenticated
    ? `${Math.round(practiceSummary?.user?.averageAccuracy || 0)}%`
    : '96%';
  const testsCompletedValue = isAuthenticated
    ? String(practiceSummary?.user?.totalTestsCompleted || 0)
    : practiceMode === 'english'
      ? '19'
      : '23';
  const challengeContent = practiceChallenge?.content || null;
  const dailyChallengeContent = dailyChallenge?.content || null;
  const activeChallengeContent = isDailyFocus && dailyChallengeContent ? dailyChallengeContent : challengeContent;

  // If daily challenge already completed, lock the editor so refresh won't reopen attempt.
  const targetText = practiceMode === 'english'
    ? (
        dailyAlreadyCompleted
          ? activeChallengeContent?.content || englishConfig.paragraph
          : activeChallengeContent?.content || englishConfig.paragraph
      )
    : (
        dailyAlreadyCompleted
          ? activeChallengeContent?.code || languageConfig.target
          : activeChallengeContent?.code || languageConfig.target
      );

  const previewText = practiceMode === 'english'
    ? activeChallengeContent?.content || englishConfig.paragraph
    : activeChallengeContent?.code || languageConfig.preview;
  const activePracticeLabel = practiceMode === 'english' ? englishConfig.label : languageConfig.label;
  const previewBadgeLabel = isEnglishMode ? englishConfig.label : languageConfig.label;
  const sidebarItems = isEnglishMode
    ? [
        { key: 'general', label: 'General English', route: 'general' },
        { key: 'technology', label: 'Technology', route: 'technology' },
        { key: 'business', label: 'Business', route: 'business' },
        { key: 'motivation', label: 'Motivation', route: 'motivation' },
        { key: 'student', label: 'Student Life', route: 'student' }
      ]
    : Object.values(LANGUAGE_DATA).map((language) => ({
        key: language.route,
        label: language.label,
        route: language.route,
        color: language.color
      }));
  const deferredTypedText = React.useDeferredValue(typedText);
  const targetWords = React.useMemo(() => targetText.trim().split(/\s+/).filter(Boolean).length, [targetText]);
  const typedWords = React.useMemo(() => deferredTypedText.trim().split(/\s+/).filter(Boolean).length, [deferredTypedText]);
  const typingMetrics = React.useMemo(
    () => buildTypingMetrics({ practiceMode, targetText, typedText: deferredTypedText, timeLimit, timeLeft }),
    [practiceMode, targetText, deferredTypedText, timeLimit, timeLeft]
  );
  const {
    accuracy,
    completedCount: completedChars,
    elapsedSeconds,
    mistakes,
    pendingCount: pendingChars,
    progressPercent,
    wpm
  } = typingMetrics;
  const isSessionFinished = Boolean(sessionResult);
  const scoreUnitLabel = practiceMode === 'english' ? 'words' : 'characters';
  const progressCountLabel = isEnglishMode
    ? `${typedWords} / ${Math.max(targetWords, 1)} words typed`
    : `${typedText.length} / ${targetText.length} characters typed`;
  const progressTitle = isEnglishMode
    ? 'Improve Your English Typing Speed and Accuracy'
    : 'Track Your Coding Typing Progress';
  const progressSubtitle = isEnglishMode
    ? 'Practice with focused English categories, grow confidence, and keep the pace steady through longer paragraphs.'
    : 'Build speed and precision with code snippets that get progressively easier to scan and type.';

  const buildSessionResult = (reason = 'manual') => {
    const isDaily = sectionKey === 'daily-challenge';
    const dailyContent = dailyChallenge?.content || null;
    const activeContent = isDaily && dailyContent ? dailyContent : practiceChallenge?.content || null;
    const activeContentType = isDaily
      ? (dailyChallenge?.challengeType || (practiceMode === 'english' ? 'english' : 'code'))
      : (practiceChallenge?.contentType || (practiceMode === 'english' ? 'english' : 'code'));
    return ({
      reason,
      wpm,
      accuracy,
      mistakes,
      completedChars,
      pendingChars,
      elapsedSeconds,
      timeLeft,
      practiceMode,
      language: languageConfig.label,
      practiceLabel: activePracticeLabel,
      progressPercent,
      contentId: activeContent?._id || activeContent?.id || '',
      contentType: activeContentType,
      difficulty: activeContent?.difficulty || '',
      category: activeContent?.category || ''
    });
  };

  const startSession = () => {
    if (!sessionResult) {
      setIsRunning(true);
    }
  };

  const resetSession = () => {
    isFinishingRef.current = false;
    setTypedText('');
    setTimeLeft(timeLimit);
    setIsRunning(false);
    setSessionResult(null);
    resetCodingEditor();
  };

  const finishSession = async (reason = 'manual') => {
    if (isFinishingRef.current) return null;
    isFinishingRef.current = true;
    setIsRunning(false);

    // Hard UI lock: if daily already completed, prevent re-submit.
    if (isDailyFocus && dailyAlreadyCompleted) {
      isFinishingRef.current = false;
      setSessionResult(null);
      return buildSessionResult(reason);
    }

    const localResult = buildSessionResult(reason);
    setSessionResult(localResult);

    if (!localResult.contentId) {
      return localResult;
    }

    setIsSubmittingResult(true);

    try {
      const response = await submitPracticeSession({
        contentId: localResult.contentId,
        contentType: localResult.contentType,
        language: practiceMode === 'english' ? 'English' : selectedLanguage,
        wpm,
        accuracy,
        mistakes,
        completionTime: elapsedSeconds
      });

      if (response?.success && response?.data) {
        setSessionResult((current) => ({
          ...current,
          ...response.data,
          reason: current?.reason || reason
        }));
        if (isDailyFocus && response.data.xpEarned != null) {
          setDailyCompletedXP(response.data.xpEarned);
        }
      }
    } catch (error) {
      // 409 = already completed today
      if (error?.status === 409 || error?.payload?.data?.alreadyCompleted) {
        setDailyCompletedXP(error?.payload?.data?.xpEarned ?? 0);
        setSessionResult(null);
      }
      // else keep local result visible
    } finally {
      setIsSubmittingResult(false);
      isFinishingRef.current = false;
    }

    return localResult;
  };

  const stats = [
    {
      label: 'Time Left',
      value: formatSeconds(timeLeft),
      icon: Clock3,
      accent: 'text-orange-600',
      bg: 'bg-orange-50'
    },
    {
      label: 'WPM',
      value: String(wpm),
      helper: wpm >= 60 ? 'Good' : 'Build speed',
      icon: Zap,
      accent: 'text-purple-600',
      bg: 'bg-purple-50'
    },
    {
      label: 'Accuracy',
      value: `${accuracy}%`,
      helper: accuracy >= 95 ? 'Excellent' : 'Improve focus',
      icon: Target,
      accent: 'text-emerald-600',
      bg: 'bg-emerald-50'
    },
    {
      label: 'Mistakes',
      value: String(mistakes),
      helper: mistakes <= 3 ? 'Low' : 'Watch spelling',
      icon: CircleAlert,
      accent: 'text-rose-600',
      bg: 'bg-rose-50'
    }
  ];

  const progressCards = [
    { label: 'Best WPM', value: bestWpmValue, color: '#a855f7', seed: 16 },
    { label: 'Best Accuracy', value: bestAccuracyValue, color: '#fb923c', seed: 29 },
    { label: 'Characters Typed', value: practiceMode === 'english' ? '214' : '156', color: '#3b82f6', seed: 42 },
    { label: 'Current Streak', value: currentStreakLabel, color: '#f97316', seed: 55, trend: isAuthenticated ? 'Synced' : '+2 this week' },
    { label: 'Tests Completed', value: testsCompletedValue, color: '#22c55e', seed: 68 }
  ];

  const challengeCopy = dailyChallenge
    ? {
        title: 'Daily Challenge',
        description: dailyChallenge.challengeType === 'english'
          ? `Type today's English paragraph and keep your speed near ${dailyChallenge.targetWPM} WPM.`
          : `Type today's code snippet and keep your speed near ${dailyChallenge.targetWPM} WPM.`,
        reward: `+${dailyChallenge.bonusXP} XP on completion`
      }
    : practiceMode === 'english'
      ? {
          title: 'Daily Challenge',
          description: 'Type the paragraph in English mode and keep your accuracy at 85% or higher.',
          reward: '+120 XP on completion'
        }
      : {
          title: 'Daily Challenge',
          description: 'Type the code snippet in JavaScript mode and score 35+ WPM.',
          reward: '+120 XP on completion'
        };

  React.useEffect(() => {
    const titleByMode = practiceMode === 'english'
      ? 'English Typing Practice'
      : `${languageConfig.label} Typing Practice`;

    const descriptionByMode = practiceMode === 'english'
      ? 'Practice English typing with SEO-friendly exercises, WPM tracking, and accuracy feedback at CodeRush Arena.'
      : `Practice ${languageConfig.label} typing with real code snippets, WPM tracking, accuracy feedback, and public leaderboard support at CodeRush Arena.`;

    applySeo({
      title: `CodeRush Arena | ${titleByMode}, Typing Test and Public Leaderboards`,
      description: descriptionByMode,
      canonicalUrl: location.pathname,
      ogType: 'website',
      ogImage: '/favicon.png',
      twitterCard: 'summary_large_image'
    });

    const existing = document.getElementById('practice-arena-jsonld');
    if (existing) {
      existing.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'practice-arena-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'CodeRush Arena',
      applicationCategory: 'EducationalApplication',
      operatingSystem: 'Web',
      description: descriptionByMode,
      url: buildAbsoluteUrl(location.pathname),
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      }
    });
    document.head.appendChild(script);

    return () => {
      const currentScript = document.getElementById('practice-arena-jsonld');
      if (currentScript) {
        currentScript.remove();
      }
    };
  }, [languageConfig.label, location.pathname, practiceMode]);

  const handleTextareaChange = (event) => {
    const nextValue = event.target.value;
    setTypedText(nextValue);

    if (sessionResult) {
      setSessionResult(null);
    }

    if (!isRunning && nextValue.length > 0) {
      startSession();
    }
  };

  const loadCodingLanguageExtension = async (language) => {
    switch (language) {
      case 'javascript': {
        const { javascript } = await import('@codemirror/lang-javascript');
        return javascript();
      }
      case 'python': {
        const { python } = await import('@codemirror/lang-python');
        return python();
      }
      case 'java': {
        const { java } = await import('@codemirror/lang-java');
        return java();
      }
      case 'cpp': {
        const { cpp } = await import('@codemirror/lang-cpp');
        return cpp();
      }
      case 'sql': {
        const { sql } = await import('@codemirror/lang-sql');
        return sql();
      }
      default: {
        const { javascript } = await import('@codemirror/lang-javascript');
        return javascript();
      }
    }
  };

  React.useEffect(() => {
    if (practiceMode !== 'coding') {
      if (typingViewRef.current) {
        typingViewRef.current.destroy();
        typingViewRef.current = null;
      }
      return;
    }

    let destroyed = false;

    const initCodingEditor = async () => {
      const langExt = await loadCodingLanguageExtension(selectedLanguage);

      if (destroyed || !typingEditorRef.current) return;

      if (typingViewRef.current) {
        typingViewRef.current.destroy();
        typingViewRef.current = null;
      }

      const view = new EditorView({
        state: EditorState.create({
          doc: '',
          extensions: [
            basicSetup,
            langExt,
            EditorView.theme({
              '&': { fontSize: '14px' },
              '.cm-scroller': { fontFamily: '"JetBrains Mono", monospace', lineHeight: '1.7', overflow: 'auto' },
              '.cm-focused': { outline: 'none' },
              '.cm-editor': { borderRadius: '0.75rem' },
              '.cm-content': { padding: '1rem', minHeight: '180px' }
            }),
            EditorView.updateListener.of((update) => {
              if (!update.docChanged) return;
              const nextValue = update.state.doc.toString();
              setTypedText(nextValue);
              if (nextValue.length > 0) {
                startSession();
              }
            })
          ]
        }),
        parent: typingEditorRef.current
      });

      typingViewRef.current = view;
    };

    initCodingEditor();

    return () => {
      destroyed = true;
      if (typingViewRef.current) {
        typingViewRef.current.destroy();
        typingViewRef.current = null;
      }
    };
  }, [practiceMode, selectedLanguage]);

  const resetCodingEditor = () => {
    if (typingViewRef.current) {
      typingViewRef.current.dispatch({
        changes: { from: 0, to: typingViewRef.current.state.doc.length, insert: '' }
      });
    }
  };

  const handlePanelResizeStart = (event) => {
    if (!practiceStackRef.current) return;

    event.preventDefault();

    const containerRect = practiceStackRef.current.getBoundingClientRect();
    const minRatio = 0.28;
    const maxRatio = 0.68;

    const updateRatio = (clientY) => {
      const relativeY = clientY - containerRect.top;
      const availableHeight = containerRect.height || 1;
      const ratio = relativeY / availableHeight;
      const clampedRatio = clamp(ratio, minRatio, maxRatio);
      setPreviewPanelRatio(clampedRatio);
    };

    const handlePointerMove = (moveEvent) => updateRatio(moveEvent.clientY);
    const handlePointerUp = (upEvent) => {
      updateRatio(upEvent.clientY);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'row-resize';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleModeChange = (modeKey) => {
    if (modeKey === 'english') {
      navigate('/practice-arena/english');
      return;
    }

    navigate(`/practice-arena/${selectedLanguage === 'javascript' ? 'coding' : selectedLanguage}`);
  };

  const handleLanguageChange = (nextLanguage) => {
    setSelectedLanguage(nextLanguage);
    setPracticeMode('coding');
    resetSession();
    navigate(`/practice-arena/${nextLanguage}`);
  };

  const handleChangeChallenge = () => {
    setChallengeNonce((current) => current + 1);
    resetSession();
  };

  const handleRestart = () => {
    resetSession();
  };

  const handleEndTest = () => {
    void finishSession('manual');
  };

  const handleAuthCtaClick = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }

    navigate('/signup', {
      state: {
        from: { pathname: location.pathname }
      }
    });
  };

  React.useEffect(() => {
    if (timeLeft === 0 && !sessionResult && (isRunning || typedText.length > 0)) {
      void finishSession('time-up');
    }
  }, [timeLeft]);
  const leaderboardTitles = [
    { key: 'daily', label: 'Daily' },
    { key: 'weekly', label: 'Weekly' },
    { key: 'allTime', label: 'All Time' }
  ];

  return (
    <main
      className="min-h-screen text-slate-900"
      style={{
        backgroundImage:
          'radial-gradient(circle at 15% 0%, rgba(124,58,237,0.14), transparent 26%), radial-gradient(circle at 92% 10%, rgba(249,115,22,0.12), transparent 22%), linear-gradient(180deg, #ffffff 0%, #fafafa 45%, #f8fafc 100%)'
      }}
    >
      {/* Mobile drawer backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 xl:hidden ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-72 overflow-y-auto bg-white shadow-2xl transition-transform duration-300 ease-in-out xl:hidden ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between border-b border-slate-200 p-4">
          <div>
            <span className="text-lg font-bold text-slate-900">CodeRush Arena</span>
            <p className="mt-0.5 text-xs text-slate-500">Navigate sections</p>
          </div>
          <button
            type="button"
            onClick={() => setMobileMenuOpen(false)}
            className="rounded-lg p-2 hover:bg-slate-100"
          >
            <X className="h-5 w-5 text-slate-700" />
          </button>
        </div>

        <div className="space-y-5 p-4">
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Navigation</p>
            <nav className="space-y-1">
              {NAV_LINKS.map((item) => {
                const active = item.path === '/practice-arena'
                  ? location.pathname.startsWith('/practice-arena')
                  : location.pathname.startsWith(item.path);
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                      active ? 'bg-purple-50 text-purple-700' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>

          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Practice Mode</p>
            <div className="space-y-1">
              {PRACTICE_MODES.map((item) => {
                const Icon = item.icon;
                const active = practiceMode === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => { handleModeChange(item.key); setMobileMenuOpen(false); }}
                    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                      active ? 'bg-purple-50 text-purple-700' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label} Practice
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">Quick Links</p>
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => { navigate('/practice-arena/daily-challenge'); setMobileMenuOpen(false); }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
              >
                <CalendarDays className="h-4 w-4" />
                Daily Challenge
              </button>
              <button
                type="button"
                onClick={() => { navigate('/practice-arena/leaderboard'); setMobileMenuOpen(false); }}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-100"
              >
                <Medal className="h-4 w-4" />
                Leaderboard
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-xl border border-orange-100 bg-orange-50 px-3 py-2.5 text-sm font-semibold text-orange-600">
            <Flame className="h-4 w-4" />
            {isAuthenticated ? currentStreakLabel : '7 Day Streak'}
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1600px] items-center gap-3 px-4 py-3 lg:px-6">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100 xl:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5 text-slate-700" />
          </button>

          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img
              src="/favicon.png"
              alt="RACE Next Step Logo"
              className="h-9 w-9 shrink-0 rounded-xl object-contain shadow-lg shadow-purple-200 sm:h-11 sm:w-11"
            />
            <div className="hidden sm:block">
              <div className="text-lg font-extrabold leading-none">CodeRush</div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">Arena</div>
            </div>
          </Link>

          <nav className="hidden flex-1 items-center justify-center gap-1 xl:flex" aria-label="Primary">
            {NAV_LINKS.map((item) => {
              const active = item.path === '/practice-arena'
                ? location.pathname.startsWith('/practice-arena')
                : location.pathname.startsWith(item.path);
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    active
                      ? 'bg-purple-50 text-purple-700'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <div className="hidden items-center gap-2 rounded-2xl border border-orange-100 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-600 lg:flex">
              <Flame className="h-4 w-4" />
              {isAuthenticated ? currentStreakLabel : '7 Day Streak'}
            </div>
            <button
              type="button"
              className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-purple-200"
            >
              <span className="max-w-[100px] truncate sm:max-w-none">{currentUserName}</span>
              <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1600px] px-4 py-4 lg:px-6 lg:py-6">
        <div className="grid gap-4 xl:grid-cols-[228px_minmax(0,1.8fr)_228px]" id="arena-layout">
          <aside className="space-y-4 order-1 xl:order-none">
            <section className="rounded-2xl border border-slate-200 bg-gradient-to-b from-purple-600 via-purple-600 to-orange-500 p-3 text-white shadow-[0_16px_60px_rgba(124,58,237,0.18)]">
              <div className="mb-3 text-sm font-bold uppercase tracking-[0.2em] text-white/75">Practice</div>
              <div className="space-y-2">
                {PRACTICE_MODES.map((item) => {
                  const Icon = item.icon;
                  const active = practiceMode === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => handleModeChange(item.key)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-semibold transition ${
                        active ? 'bg-white text-slate-900 shadow-lg' : 'text-white/90 hover:bg-white/10'
                      }`}
                    >
                      <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${active ? 'bg-purple-100 text-purple-700' : 'bg-white/12 text-white'}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span>{item.label} Practice</span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 grid gap-2 text-sm font-semibold text-white/90">
                <button
                  type="button"
                  onClick={() => navigate('/practice-arena/daily-challenge')}
                  className="flex items-center gap-3 rounded-xl bg-white/10 px-3 py-2.5 transition hover:bg-white/15"
                >
                  <CalendarDays className="h-4 w-4" />
                  Daily Challenge
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/practice-arena/leaderboard')}
                  className="flex items-center gap-3 rounded-xl bg-white/10 px-3 py-2.5 transition hover:bg-white/15"
                >
                  <Medal className="h-4 w-4" />
                  Leaderboard
                </button>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Language</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">Choose your track</div>
                </div>
                <Languages className="h-4 w-4 text-purple-600" />
              </div>

              <div className="space-y-2">
                {sidebarItems.map((item) => {
                  const active = isEnglishMode ? englishTopic === item.route : selectedLanguage === item.route;
                  const colorClass = isEnglishMode
                    ? ENGLISH_BADGE_COLORS[item.key] || 'from-purple-600 to-pink-500'
                    : item.color;

                  return (
                    <button
                      key={item.route}
                      type="button"
                      onClick={() => {
                        if (isEnglishMode) {
                          setEnglishTopic(item.route);
                          resetSession();
                          return;
                        }

                        handleLanguageChange(item.route);
                      }}
                      className={`flex w-full items-center justify-between rounded-xl border px-3 py-3 text-left text-sm font-semibold transition ${
                        active
                          ? 'border-purple-300 bg-purple-50 text-purple-700 shadow-sm'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-purple-200 hover:bg-slate-50'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span className={`flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br ${colorClass} text-white shadow-sm`}>
                          {item.label.slice(0, 2)}
                        </span>
                        {item.label}
                      </span>
                      {active ? <CheckCircle2 className="h-4 w-4 text-purple-600" /> : null}
                    </button>
                  );
                })}
              </div>
            </section>

            {isAuthenticated ? (
              <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-bold text-purple-700">
                    <Zap className="h-4 w-4" />
                    Your XP
                  </div>
                  <span className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-bold text-purple-700">Level {currentLevel}</span>
                </div>
                <div className="text-3xl font-extrabold tracking-tight text-slate-900">{totalXP.toLocaleString()} <span className="text-base font-semibold text-slate-400">XP</span></div>
                <div className="mt-3">
                  <div className="mb-1 flex items-center justify-between text-xs font-semibold text-slate-500">
                    <span>{xpIntoLevel} / {XP_PER_LEVEL} to Level {currentLevel + 1}</span>
                    <span>{xpProgressPercent}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-purple-500 to-orange-400 transition-all duration-500"
                      style={{ width: `${xpProgressPercent}%` }}
                    />
                  </div>
                </div>
              </section>
            ) : null}

          </aside>

          <section id="practice-workspace" className="min-w-0 space-y-4 order-2 xl:order-none">
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex shrink-0 rounded-2xl border border-slate-200 bg-white p-0.5 shadow-sm">
                {PRACTICE_MODES.map((item) => {
                  const active = practiceMode === item.key;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => handleModeChange(item.key)}
                      className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition ${
                        active
                          ? 'bg-gradient-to-r from-purple-600 to-orange-500 text-white shadow-md'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </button>
                  );
                })}
              </div>

              <div className="flex shrink-0 items-center gap-1.5 rounded-2xl border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">
                <Timer className="h-3.5 w-3.5 text-slate-500" />
                <label htmlFor="timeLimit" className="sr-only">Select time limit</label>
                <select
                  id="timeLimit"
                  value={timeLimit}
                  onChange={(event) => {
                    const nextValue = Number(event.target.value);
                    setTimeLimit(nextValue);
                    setTimeLeft(nextValue);
                    setTypedText('');
                    setIsRunning(false);
                    setSessionResult(null);
                  }}
                  className="bg-transparent text-xs font-semibold text-slate-700 outline-none"
                >
                  {TIME_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}s
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                onClick={handleChangeChallenge}
                className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-purple-200 hover:text-purple-700"
              >
                <RefreshCcw className="h-3.5 w-3.5" />
                Change Challenge
              </button>

              <button
                type="button"
                onClick={isRunning ? handleEndTest : startSession}
                className={`inline-flex shrink-0 items-center gap-2 rounded-2xl px-3 py-2 text-xs font-semibold shadow-sm transition ${
                  isRunning
                    ? 'border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
                  : 'border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                }`}
              >
                {isSubmittingResult ? 'Saving...' : isRunning ? 'End Practice' : 'Start Practice'}
              </button>

            </div>

              <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                {stats.map((item) => {
                  const Icon = item.icon;
                  return (
                  <article key={item.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{item.label}</div>
                        <div className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">{item.value}</div>
                        {item.helper ? <div className="mt-1 text-sm font-medium text-emerald-600">{item.helper}</div> : null}
                      </div>
                      <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${item.bg}`}>
                        <Icon className={`h-5 w-5 ${item.accent}`} />
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="space-y-4">
              <div className="min-w-0 space-y-4">
                <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-[0.2em] text-purple-600">
                        TYPE THE FOLLOWING {practiceMode === 'english' ? 'PARAGRAPH' : 'CODE'}
                      </div>
                      <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">
                        {practiceMode === 'english'
                          ? 'Improve Your English Typing Speed and Accuracy'
                          : `${activePracticeLabel} practice for speed, accuracy, and public leaderboard scoring`}
                      </h1>
                      {practiceMode === 'english' ? (
                        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                          Practice structured English categories to improve speed, accuracy, and consistency across longer sessions.
                        </p>
                      ) : (
                        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                          Focus on code rhythm, syntax, and precision so longer snippets feel more manageable and natural to type.
                        </p>
                      )}
                    </div>
                  </div>

                  {isDailyFocus && dailyAlreadyCompleted ? (<div className="mt-6 flex flex-col items-center gap-4 rounded-2xl border border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50 p-8 text-center"><div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-100"><Trophy className="h-8 w-8 text-orange-500" /></div><div><h2 className="text-xl font-extrabold text-slate-900">You have completed today challenge!</h2><p className="mt-2 text-sm leading-6 text-slate-600">You earned <span className="font-bold text-orange-600">+{dailyCompletedXP ?? dailyChallenge?.bonusXP ?? 120} XP</span> today. Come back tomorrow for a new challenge.</p></div><div className="flex flex-wrap items-center justify-center gap-3"><button type="button" onClick={() => navigate('/practice-arena/leaderboard')} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:brightness-105"><Trophy className="h-4 w-4" />See Leaderboard</button><button type="button" onClick={() => navigate('/practice-arena')} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"><RefreshCcw className="h-4 w-4" />Free Practice</button></div><p className="text-xs text-slate-400">Resets daily at midnight</p></div>) : (<div
                    ref={practiceStackRef}
                    className="mt-4 flex flex-col gap-0"
                  >
                    <article
                      className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50"
                    >
                      <div className="border-b border-slate-200 px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 text-sm font-bold text-purple-700">
                            <Code2 className="h-4 w-4" />
                            {practiceMode === 'english' ? 'Live paragraph preview' : 'Code preview'}
                          </div>
                          <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-white px-3 py-1 text-xs font-semibold text-purple-700">
                            <Keyboard className="h-3.5 w-3.5" />
                            {isFetchingChallenge ? 'Loading...' : previewBadgeLabel}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-1 min-h-0 p-4">
                        {practiceMode === 'english' ? (
                          <div className="flex h-full min-h-0 flex-1 flex-col overflow-auto rounded-2xl border border-dashed border-slate-300 bg-white p-4 text-[15px] leading-8 text-slate-700">
                            <p className="font-medium">{previewText}</p>
                            <p className="mt-3 text-sm text-slate-500">{englishConfig.subtitle}</p>
                          </div>
                        ) : (
                          <div className="flex h-full min-h-0 flex-1 overflow-auto">
                            <SyntaxHighlighter
                              language={selectedLanguage}
                              style={oneLight}
                              customStyle={{
                                margin: 0,
                                width: '100%',
                                borderRadius: '1rem',
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                padding: '1rem',
                                fontSize: '14px',
                                lineHeight: '1.7'
                              }}
                              showLineNumbers
                              wrapLongLines
                            >
                              {previewText}
                            </SyntaxHighlighter>
                          </div>
                        )}
                      </div>
                    </article>

                    <div
                      role="separator"
                      aria-orientation="horizontal"
                      aria-label="Resize code preview and typing area"
                      onPointerDown={handlePanelResizeStart}
                      className="group relative flex h-4 cursor-row-resize items-center justify-center"
                    >
                      <div className="h-px w-full bg-slate-200 transition-colors group-hover:bg-purple-300" />
                      <div className="absolute inset-x-0 top-1/2 h-4 -translate-y-1/2" />
                    </div>

                    <article
                      className="flex flex-col overflow-hidden rounded-2xl border border-purple-200 bg-white shadow-[0_8px_40px_rgba(124,58,237,0.08)]"
                    >
                      <div className="border-b border-purple-100 bg-gradient-to-r from-purple-50 to-orange-50 px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 text-sm font-bold text-purple-700">
                            {isDailyFocus ? (
                              <>
                                <CalendarDays className="h-4 w-4 text-orange-500" />
                                <span className="text-orange-600">Daily Challenge</span>
                                <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-600">Today</span>
                              </>
                            ) : (
                              <span>TYPE HERE</span>
                            )}
                          </div>
                          <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-500 shadow-sm">
                            Split View
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col p-4 gap-3">
                        {practiceMode === 'english' ? (
                          <>
                            <label htmlFor="typing-area" className="sr-only">Typing editor</label>
                            <textarea
                              id="typing-area"
                              value={typedText}
                              onChange={handleTextareaChange}
                              placeholder="Start typing the paragraph here..."
                              readOnly={isSessionFinished}
                              className="w-full min-h-[200px] resize-none rounded-2xl border border-slate-200 bg-white p-4 font-mono text-[15px] leading-8 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-purple-300 focus:ring-4 focus:ring-purple-100 disabled:cursor-not-allowed disabled:bg-slate-50"
                            />
                          </>
                        ) : (
                          <div
                            ref={typingEditorRef}
                            className="w-full min-h-[220px] rounded-2xl border border-slate-200 focus-within:border-purple-300 focus-within:ring-4 focus-within:ring-purple-100"
                            style={{ pointerEvents: isSessionFinished ? 'none' : 'auto', opacity: isSessionFinished ? 0.6 : 1 }}
                          />
                        )}

                        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                          <div className="flex flex-wrap items-center gap-3 text-sm font-semibold">
                            <span className="flex items-center gap-2 text-emerald-600">
                              <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                              Correct {completedChars} {scoreUnitLabel}
                            </span>
                            <span className="flex items-center gap-2 text-rose-600">
                              <span className="h-2.5 w-2.5 rounded-full bg-rose-500" />
                              Incorrect {mistakes} {scoreUnitLabel}
                            </span>
                            <span className="flex items-center gap-2 text-slate-500">
                              <span className="h-2.5 w-2.5 rounded-full bg-slate-400" />
                              Pending {pendingChars} {scoreUnitLabel}
                            </span>
                          </div>

                          <div className="flex flex-wrap items-center gap-3">
                            <button
                              type="button"
                              onClick={handleRestart}
                              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-5 py-3 text-sm font-semibold text-purple-700 transition hover:border-purple-200 hover:bg-purple-50"
                            >
                              <RefreshCcw className="h-4 w-4" />
                              Restart
                            </button>
                            <button
                              type="button"
                              onClick={handleEndTest}
                              className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-pink-500 to-orange-500 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-pink-100 transition hover:brightness-105"
                            >
                              <span className="h-3 w-3 rounded-sm bg-white/95" />
                              End Practice
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  </div>)}  
                  
                </section>

                {sessionResult ? (
                  <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">
                          Result Showcase
                        </div>
                        <h2 className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900">
                          {sessionResult.reason === 'time-up' ? 'Time is up' : 'Test ended'}
                        </h2>
                        <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-600">
                          Your final score is locked in below. Restart to run the same challenge again or change the challenge to switch content.
                        </p>
                      </div>
                      <div className="rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-emerald-700 shadow-sm">
                        Progress {Math.round(progressPercent)}%
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
                      {[
                        { label: 'WPM', value: sessionResult.wpm },
                        { label: 'Accuracy', value: `${sessionResult.accuracy}%` },
                        { label: 'Mistakes', value: sessionResult.mistakes },
                        {
                          label: `Correct ${sessionResult.contentType === 'english' ? 'Words' : 'Chars'}`,
                          value: sessionResult.completedChars
                        },
                        {
                          label: 'XP Earned',
                          value: sessionResult.xpEarned ? `+${sessionResult.xpEarned} XP` : '+10 XP'
                        },
                        ...(isAuthenticated && sessionResult.user?.totalXP != null ? [{
                          label: 'Total XP',
                          value: `${sessionResult.user.totalXP.toLocaleString()} XP`
                        }] : [])
                      ].map((item) => (
                        <article key={item.label} className="rounded-2xl border border-emerald-100 bg-white p-4">
                          <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">{item.label}</div>
                          <div className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">{item.value}</div>
                        </article>
                      ))}
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={handleRestart}
                        className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
                      >
                        <RefreshCcw className="h-4 w-4" />
                        Try Again
                      </button>
                      <button
                        type="button"
                        onClick={handleChangeChallenge}
                        className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-50"
                      >
                        <ArrowRight className="h-4 w-4" />
                        Next Challenge
                      </button>
                    </div>
                  </section>
                ) : null}

                {!isAuthenticated ? (
                  <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="bg-gradient-to-br from-purple-50 to-orange-50 p-4">
                      <div className="mb-3 flex justify-center">
                        <Trophy className="h-11 w-11 text-orange-500" />
                      </div>
                      <div className="text-center">
                        <h2 className="text-base font-bold text-slate-900">Login to save your score</h2>
                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          Compete on the leaderboard and track your progress across sessions.
                        </p>
                      </div>
                    </div>
                    <div className="p-4">
                      <button
                        type="button"
                        onClick={handleAuthCtaClick}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-orange-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-purple-200 transition hover:brightness-105"
                      >
                        Login / Sign Up
                        <ArrowRight className="h-4 w-4" />
                      </button>
                    </div>
                  </section>
                ) : null}
              </div>

            </div>

          </section>

          <aside className="space-y-4 order-3 xl:order-none">
            <Link
              ref={dailyChallengeRef}
              to="/practice-arena/daily-challenge"
              className={`block w-full overflow-hidden rounded-2xl border bg-white shadow-sm transition-all text-left ${
                isDailyChallengeFocus
                  ? 'border-orange-400 shadow-[0_0_0_3px_rgba(251,146,60,0.25)] ring-2 ring-orange-300'
                  : dailyChallenge?.completedToday || dailyCompletedXP !== null
                    ? 'border-emerald-300 hover:border-emerald-400 hover:shadow-md'
                    : 'border-slate-200 hover:border-orange-300 hover:shadow-md'
              }`}
            >
              <div className="border-b border-slate-100 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-orange-500">
                    <CalendarDays className="h-4 w-4" />
                    {challengeCopy.title}
                  </div>
                  {dailyChallenge?.completedToday || dailyCompletedXP !== null ? (
                    <div className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Done
                    </div>
                  ) : (
                    <div className="rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-600">
                      {formatCountdown(Math.max(0, Math.floor(dailyCountdownMs / 1000)))}
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-3 p-3">
                <p className="text-sm leading-6 text-slate-700">{challengeCopy.description}</p>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full rounded-full bg-gradient-to-r ${dailyChallenge?.completedToday || dailyCompletedXP !== null ? 'from-emerald-400 to-teal-500 w-full' : 'from-orange-400 to-pink-500 w-[78%]'}`} />
                </div>
                <div className="flex items-center justify-between text-sm font-semibold text-slate-600">
                  <span>1/1</span>
                  <span className={`flex items-center gap-2 ${dailyChallenge?.completedToday || dailyCompletedXP !== null ? 'text-emerald-600' : 'text-orange-600'}`}>
                    <Award className="h-4 w-4" />
                    {dailyChallenge?.completedToday || dailyCompletedXP !== null
                      ? `+${dailyCompletedXP ?? dailyChallenge?.bonusXP ?? 120} XP earned`
                      : challengeCopy.reward}
                  </span>
                </div>
              </div>
            </Link>

            <section
              ref={leaderboardRef}
              className={`rounded-2xl border bg-white p-4 shadow-sm transition-all ${
                isLeaderboardFocus
                  ? 'border-purple-400 shadow-[0_0_0_3px_rgba(168,85,247,0.2)] ring-2 ring-purple-300'
                  : 'border-slate-200'
              }`}
            >
              <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-orange-500">
                <Trophy className="h-4 w-4" />
                Leaderboard
              </div>

              <div className="mb-4 flex items-center gap-1 rounded-xl bg-slate-100 p-1 text-sm font-semibold text-slate-500">
                {leaderboardTitles.map((item) => {
                  const active = leaderboardTab === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setLeaderboardTab(item.key)}
                      className={`flex-1 rounded-lg px-3 py-2 transition ${
                        active ? 'bg-white text-slate-900 shadow-sm' : 'hover:text-slate-700'
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </div>

              <div className="space-y-2">
                {leaderboardRows.map((row, index) => {
                  const rank = index + 1;
                  const rowKey = row.userId ? String(row.userId) : `${row.name || 'user'}-${index}`;
                  return (
                    <div
                      key={rowKey}
                      className={`flex items-center justify-between rounded-xl border px-3 py-2.5 text-sm ${
                        row.highlight
                          ? 'border-purple-200 bg-purple-50'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                            rank === 1
                              ? 'bg-amber-100 text-amber-700'
                              : rank === 2
                                ? 'bg-slate-100 text-slate-700'
                                : rank === 3
                                  ? 'bg-orange-100 text-orange-700'
                                  : row.highlight
                                    ? 'bg-purple-200 text-purple-700'
                                    : 'bg-slate-100 text-slate-500'
                          }`}
                        >
                          {rank}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate font-semibold text-slate-900">{row.name || row.username || 'User'}</div>
                          {row.highlight ? <div className="text-xs text-purple-600">Current best effort</div> : null}
                        </div>
                      </div>
                      <div className="ml-2 shrink-0 font-semibold text-slate-700">{row.wpm ?? row.bestWPM ?? 0} WPM</div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-purple-700 transition hover:border-purple-200 hover:bg-purple-50"
              >
                View Full Leaderboard
                <ArrowRight className="h-4 w-4" />
              </button>
            </section>
          </aside>
        </div>

        <section className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              icon: LayoutDashboard,
              title: 'Track Progress',
              text: 'See your improvement over time with repeat practice and public scorekeeping.',
              color: 'from-purple-500 to-violet-600',
              bg: 'bg-purple-50',
              accent: 'text-purple-700'
            },
            {
              icon: Flame,
              title: 'Build Streaks',
              text: 'Practice daily and keep the streak alive with lightweight engagement loops.',
              color: 'from-orange-400 to-pink-500',
              bg: 'bg-orange-50',
              accent: 'text-orange-700'
            },
            {
              icon: Target,
              title: 'Improve Speed',
              text: 'Increase typing speed and consistency across code and language drills.',
              color: 'from-emerald-400 to-teal-500',
              bg: 'bg-emerald-50',
              accent: 'text-emerald-700'
            },
            {
              icon: Gamepad2,
              title: 'Share Score',
              text: 'Compare results with friends and future public challenge modes.',
              color: 'from-sky-400 to-blue-500',
              bg: 'bg-sky-50',
              accent: 'text-sky-700'
            }
          ].map((card) => {
            const Icon = card.icon;
            return (
              <article key={card.title} className="group rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5">
                <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${card.color} shadow-sm`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{card.title}</h3>
                <p className="mt-1.5 text-sm leading-6 text-slate-500">{card.text}</p>
              </article>
            );
          })}
        </section>

        <section className="mt-8 grid gap-4 lg:grid-cols-2">
          <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-purple-600">
              <BookOpen className="h-4 w-4" />
              What Is a Typing Test?
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">
              Practice that feels useful, not decorative.
            </h2>
            <p className="mt-3 text-sm leading-7 text-slate-600">
              A typing test measures how quickly and accurately you can reproduce text under a time limit.
              In CodeRush Arena, that text can be code or English prose, which keeps the session practical for students,
              developers, professionals, and job seekers.
            </p>
          </article>

          <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-orange-500">
              <GraduationCap className="h-4 w-4" />
              How Scoring Works
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">
              WPM and accuracy stay visible the whole time.
            </h2>
            <p className="mt-3 text-sm leading-7 text-slate-600">
              WPM is calculated from the number of correct characters you enter over time. Accuracy compares your
              typed text against the reference text. Mistakes are tracked char by char, so users can improve both speed and precision.
            </p>
          </article>
        </section>

        <section className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-emerald-600">
              <Keyboard className="h-4 w-4" />
              Benefits of Coding Practice
            </div>
            <p className="mt-3 text-sm leading-7 text-slate-600">
              Coding typing practice helps you work faster through arrays, loops, conditions, and query syntax.
              It is especially helpful when interview prep, daily development, and technical assessments demand
              accurate keyboard control.
            </p>
          </article>

          <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-sky-600">
              <Languages className="h-4 w-4" />
              Benefits of English Practice
            </div>
            <p className="mt-3 text-sm leading-7 text-slate-600">
              English typing practice supports writing fluency for assignments, messages, documentation, and general
              productivity. It also gives learners a simple way to improve typing speed without switching contexts.
            </p>
          </article>

          <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-rose-600">
              <Sparkles className="h-4 w-4" />
              Future Public Modes
            </div>
            <p className="mt-3 text-sm leading-7 text-slate-600">
              The architecture is ready for memory games, reflex taps, daily puzzles, streak systems, achievement badges,
              and public leaderboards without changing the core experience.
            </p>
          </article>
        </section>

        <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              {
                title: 'How WPM is calculated',
                text: 'English mode counts correct words per minute, while coding mode counts correct characters and converts them into five-character words.'
              },
              {
                title: 'How accuracy is calculated',
                text: 'English mode compares words case-insensitively, while coding mode compares characters exactly as typed.'
              },
              {
                title: 'Why code typing matters',
                text: 'Technical practice improves confidence with symbols, indentation, and common programming structures.'
              },
              {
                title: 'Why session duration matters',
                text: 'Short, repeatable sessions help the product drive retention, daily active use, and organic search traffic.'
              }
            ].map((item) => (
              <article key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <h3 className="text-base font-bold text-slate-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-7 text-slate-600">{item.text}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

export default PracticeArenaPage;


